// Pure credential-verdict predicates for the known-model add flow.
// Per rule: ai-code-requires-tests, reference-rules-in-comments (ai.knowledge/global).

import { toConfiguration } from '@studio/models/video';
import type { Uri } from '@studio/models/net';

/** Auth signals for a known-model INVALID site, plus the operator Skip flag. */
export interface AuthVerdictInput {
  /** transient controlAuthenticationStatus / videoAuthenticationStatus === INVALID */
  statusInvalid: boolean;
  /** synchronous previews.*.error matched 400/401/403 (isCausedBy) */
  errorAuthFail: boolean;
  /** persistent add-store flag set by the Skip button */
  authSkipped: boolean;
}

/** True ⇢ block on the credentials modal: an auth signal fired and not skipped. */
export const shouldBlockForAuth = ({
  statusInvalid,
  errorAuthFail,
  authSkipped
}: AuthVerdictInput): boolean => (statusInvalid || errorAuthFail) && !authSkipped;

/** True ⇢ an auth-failed probe should PROCEED because the operator skipped
 *  (complement of shouldBlockForAuth over the same two auth signals). */
export const shouldProceedOnSkippedAuth = ({
  statusInvalid,
  errorAuthFail,
  authSkipped
}: AuthVerdictInput): boolean => (statusInvalid || errorAuthFail) && authSkipped;

// Per rules: ai-code-requires-tests, reference-rules-in-comments (ai.knowledge/global).

/**
 * Where a single-add ONVIF probe stands on the camera's RTSP stream URI:
 *   - 'pending': no answer yet (no capabilities, no terminal error, and the stall
 *                timeout has not elapsed), or the camera rejected the credentials
 *                and the probe waits behind the credentials modal.
 *   - 'ready':   capabilities carry a `discovery.uri` that maps to a video
 *                configuration, so discovery.tsx can build `candidate.video`.
 *   - 'missing': the probe settled WITHOUT a usable stream: it failed for a
 *                non-credential reason, capabilities arrived with no URI (or one
 *                `toConfiguration` cannot map), or it stalled.
 *
 * The inputs are read in discovery.tsx's order: a control preview error first,
 * then the published capabilities, then the stall. Discovery builds no video for
 * an errored probe, so a footer that let capabilities outrank the error would
 * wait for a video that never comes.
 *
 * 'missing' is the path for a camera that really returns no usable stream URI.
 * The bridge's ONVIF builder sends one GetStreamUri request, for the first media
 * profile (bridge/encoder/pkg/driver/onvif/builder.go `discoverVideoSources`); on
 * failure it logs and still builds the driver, so a PTZ camera still reports
 * movement capabilities. A camera can also report a URI this flow cannot use (an
 * http:// one). The builder's GetProfiles call (authenticated, before
 * GetStreamUri) has succeeded by then, so 'missing' is NOT a credential
 * rejection: the stream address has to come from the user, in the RTSP helper.
 * Reading "any capabilities" as success would close the credentials modal and
 * advance with no video configuration.
 *
 * 'missing' does not depend on capabilities alone: a fixed (non-PTZ) camera
 * whose GetStreamUri fails publishes NO capabilities at all —
 * protobuf/control.ts `pbToJsControlCapabilities` returns undefined when there
 * is neither a URI nor movement — and a probe that errors never publishes them.
 * Without `settlement` both would read as 'pending' forever and the Source
 * step's Continue gate would never open.
 *
 * Per rules: ai-code-requires-tests, reference-rules-in-comments (ai.knowledge/global).
 */
export type OnvifStreamVerdict = 'pending' | 'ready' | 'missing';

/** Probe outcomes besides published capabilities, in the order the verdict reads them. */
export interface OnvifProbeSettlement {
  /** The control preview errored with a credential rejection (400/401/403). The
   *  probe stays 'pending' behind the credentials modal, whatever it published. */
  credentialsRejected?: boolean | undefined;
  /** The control preview errored for a reason OTHER than rejected credentials. */
  probeFailed?: boolean | undefined;
  /** The probe attempt published nothing within discovery.tsx's stall timeout. */
  stalled?: boolean | undefined;
}

/** True when an ONVIF-reported stream URI maps to a video configuration this flow can edit. */
const isUsableStreamUri = (uri: unknown): boolean =>
  typeof uri === 'object' && uri !== null && toConfiguration(uri as Uri) !== undefined;

/** Classify an ONVIF probe by whether it produced a usable stream URI. */
export const resolveOnvifStreamVerdict = (
  capabilities: { readonly discovery?: { readonly uri?: unknown } | undefined } | undefined,
  { credentialsRejected, probeFailed, stalled }: OnvifProbeSettlement = {}
): OnvifStreamVerdict => {
  // A probe error outranks capabilities, as in discovery.tsx's error branch.
  if (credentialsRejected) return 'pending';
  if (probeFailed) return 'missing';

  // Published capabilities outrank the stall: a late URI still wins.
  if (capabilities) {
    return isUsableStreamUri(capabilities.discovery?.uri) ? 'ready' : 'missing';
  }

  return stalled ? 'missing' : 'pending';
};

/** Inputs to isAuthTrulySuccessful — all plain booleans read off the selector. */
export interface AuthSuccessInput {
  /** Legacy ONVIF success: capabilities arrived AND carry a stream URI
   *  (`resolveOnvifStreamVerdict(...) === 'ready'`). Movement-only capabilities
   *  (verdict 'missing') are not success — see OnvifStreamVerdict. */
  legacyCapabilities: boolean;
  /** video tmp per-aspect Authentication === VALIDATED (RTSP driver accepted). */
  videoAuthValidated: boolean;
  /** control (HTTP) plane proceeded: isKnownModelHttpOk && !authenticate. */
  controlValidated: boolean;
  /** the candidate's video plane carries credentials → the video plane
   *  authenticates, so control success ALONE must not close the modal. */
  videoAuthGating: boolean;
}

/** True ⇢ close the credentials modal because auth truly succeeded. The
 *  control-plane arm is gated on `!videoAuthGating` so that on a video-drives-
 *  auth camera the modal stays open until the video plane validates. */
export const isAuthTrulySuccessful = ({
  legacyCapabilities,
  videoAuthValidated,
  controlValidated,
  videoAuthGating
}: AuthSuccessInput): boolean =>
  legacyCapabilities || videoAuthValidated || (controlValidated && !videoAuthGating);

/** Inputs to shouldShowAuthError — all plain booleans read off the selector. */
export interface AuthErrorInput {
  /** !!state.authError — an "Invalid username or password" string is set. */
  hasAuthError: boolean;
  /** the operator clicked Authenticate and no terminal verdict has landed. */
  isAuthenticating: boolean;
  /** video tmp per-aspect Authentication === VALIDATING (fresh probe in flight). */
  videoAuthValidating: boolean;
  /** the verdict being read is STALE for the submitted creds — the tmp predates
   *  them (its URX carries no user:pass@). See isVerdictStaleForCredentials. */
  verdictStale: boolean;
}

/** True ⇢ surface the "Invalid username or password" error: only on a fresh
 *  terminal INVALID, never during the resync window (stale tmp) or while a
 *  fresh probe is still VALIDATING. */
export const shouldShowAuthError = ({
  hasAuthError,
  isAuthenticating,
  videoAuthValidating,
  verdictStale
}: AuthErrorInput): boolean =>
  hasAuthError && !(isAuthenticating && (videoAuthValidating || verdictStale));

/**
 * Hint shown UNDER the "Invalid username or password" error when that INVALID
 * came from the video plane (RTSP). Some cameras (Sony SRG-X120, measured
 * 2026-10-08) refuse every login for ~60s after repeated failures, and bridges
 * older than the lockout fix report that as INVALID even for the correct
 * password. The error is still shown at once, so a mistyped password gets
 * immediate feedback; the hint covers the lockout case. The verdict is
 * non-sticky: if the camera later accepts the credentials it reports VALIDATED
 * and the modal closes on its own (isAuthTrulySuccessful while authenticating).
 */
export const AUTH_LOCKOUT_HINT =
  'If you are sure these are correct, the camera may be briefly blocking sign-ins after repeated attempts (usually about a minute). This dialog closes on its own once the camera accepts them, or you can continue anyway.';

/** Inputs to resolveLockoutHint. */
export interface LockoutHintInput {
  /** the "Invalid username or password" error is currently displayed. */
  errorShown: boolean;
  /** the INVALID came from the video tmp's own verdict (the RTSP plane, where
   *  camera login lockouts surface). Control-plane (HTTP) rejections are
   *  definitive and get no hint, even when the video plane also carries the
   *  credentials. */
  videoPlaneInvalid: boolean;
}

/** The lockout hint for the credentials modal, or null. */
export const resolveLockoutHint = ({
  errorShown,
  videoPlaneInvalid
}: LockoutHintInput): string | null => (errorShown && videoPlaneInvalid ? AUTH_LOCKOUT_HINT : null);

/** True ⇢ the verdict read from a probe tmp is stale for the submitted creds:
 *  creds are entered but the tmp's URX still carries no userinfo (user:pass@),
 *  so its INVALID must not be treated as a credential rejection. The regex
 *  matches userinfo in the authority only; the `/` is escaped (`\/`) so the
 *  vendored esbuild test transformer doesn't mis-tokenize the regex terminator.
 *  Feed staleness ONLY into shouldShowAuthError, never shouldBlockForAuth — else
 *  `authenticate` flips false and the reverted premature modal-close bug returns. */
export const isVerdictStaleForCredentials = (
  tmpUrx: string | undefined,
  hasCredentials: boolean
): boolean => hasCredentials && !/:\/\/[^\/@\s]+@/.test(tmpUrx ?? '');
